<template>
  <div class="tx-list-container">
    <div class="filter-area">
      <div class="ml-2 mt-2">
        <div
          class="
            is-flex-direction-column
            is-align-items-center
            is-justify-content-space-between
            mb-2
          "
        >
          <div class="mb-1">
            <strong>{{ $gettext("Select timespan:") }}</strong>
          </div>
          <div class="datepicker-export">
            <date-picker
              v-model:value="exportDate"
              :open="datePickerShow ? true : null"
              range
              prefix-class="xmx"
              :editable="false"
              :placeholder="$gettext('All transactions')"
              @clear="
                () => {
                  selectedTimeSpanType = ''
                  datePickerShow = false
                }
              "
              @change="datePickerShow = selectedTimeSpanType ? true : false"
              @pick="selectedTimeSpanType = ''"
              :disabled-date="disabledDates"
            >
              <template #header="{ emit }">
                <div>
                  <div
                    v-for="selector in selectorsOrder"
                    :class="{
                      selected: selector == selectedTimeSpanType,
                    }"
                    class="timespan"
                  >
                    <button
                      class="xmx-btn xmx-btn-text"
                      @click="
                        () => {
                          selectedTimeSpanOffset =
                            selectedTimeSpanType != selector
                              ? -1
                              : selectedTimeSpanOffset - 1
                          selectedTimeSpanType = selector
                          emit(selectedTimeSpan)
                        }
                      "
                    >
                      <i class="xmx-icon-left"></i>
                    </button>
                    <button
                      class="xmx-btn xmx-btn-text"
                      @click="
                        () => {
                          selectedTimeSpanType = selector
                          selectedTimeSpanOffset = 0
                          emit(selectedTimeSpan)
                        }
                      "
                    >
                      {{ selectorLabels[selector] }}
                    </button>
                    <button
                      class="xmx-btn xmx-btn-text"
                      @click="
                        ;[selectedTimeSpanOffset++, emit(selectedTimeSpan)]
                      "
                      :class="{
                        hide:
                          selectedTimeSpanType != selector ||
                          isSelectionCurrent,
                      }"
                    >
                      <i class="xmx-icon-right"></i>
                    </button>
                    <button
                      class="xmx-btn xmx-btn-text confirm"
                      @click="datePickerShow = false"
                      :class="{ hide: selectedTimeSpanType != selector }"
                    >
                      {{ $gettext("confirm") }}
                    </button>
                  </div>
                </div>
              </template>
            </date-picker>
          </div>
          <div class="mb-1 mt-3">
            <strong>{{ $gettext("Select recipient:") }}</strong>
          </div>
          <div class="recipient-filter is-flex is-flex-direction-row">
            <div class="recipient-filter-input">
              <model-list-select
                :list="
                  recipientBatchLoader.elements.reduce((acc, r, idx) => {
                    if (recipient?.internalId !== r.internalId)
                      acc.push({ name: r.name, idx })
                    return acc
                  }, [])
                "
                option-value="idx"
                option-text="name"
                v-model="selectedRecipientIdx"
                :placeholder="$gettext('All recipient')"
                @searchchange="onRecipientSearch"
                id="recipientSelector"
              >
              </model-list-select>
            </div>
            <div>
              <button
                class="recipient-filter-reset"
                :class="{ disable: selectedRecipientIdx === null }"
                @click="selectedRecipientIdx = null"
              >
                <fa-icon
                  class="refreshing"
                  v-if="recipientBatchLoader.isNewBatchLoading"
                  icon="sync"
                ></fa-icon>
                <fa-icon
                  v-else-if="selectedRecipientIdx !== null"
                  icon="fa-xmark"
                >
                </fa-icon>
                <fa-icon v-else icon="fa-user"></fa-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="container is-fluid custom-heavy-line-separator"></div>
    </div>
    <section
      ref="transactionsContainer"
      @scroll="transactionBatchLoader.getNextElements"
      class="modal-card-body"
    >
      <div class="tx-list">
        <div
          class="
            custom-card
            is-flex-direction-column
            is-align-items-center
            is-justify-content-space-between
            mb-4
          "
        >
          <TransactionItem
            v-for="transaction in transactionBatchLoader.elements"
            :key="transaction"
            :transaction="transaction"
            :account="account"
          />
          <Loading
            v-if="transactionBatchLoader.isNewBatchLoading"
            v-model:active="transactionBatchLoader.isNewBatchLoading"
            class="loader-container"
            :can-cancel="false"
            :is-full-page="false"
            :width="30"
            :height="30"
          />
          <div
            v-else-if="
              transactionBatchLoader.elements.length == 0 &&
              !transactionBatchLoader.isNewBatchLoading.value
            "
            class="is-flex is-align-items-center is-justify-content-center"
          >
            {{ $gettext("No transaction found") }}
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script lang="ts">
  import { mapGetters } from "vuex"

  import { Options, Vue } from "vue-class-component"
  import Loading from "vue-loading-overlay"
  import DatePicker from "vue-datepicker-next"

  import { ModelListSelect } from "vue-search-select"
  import moment from "moment"

  import TransactionItem from "./TransactionItem.vue"

  import { UIError } from "../exception"

  // Assets

  import "vue-datepicker-next/index.css"
  import "vue-search-select/dist/VueSearchSelect.css"
  import "@/assets/datepicker.scss"

  import { mapModuleState } from "@/utils/vuex"
  import UseBatchLoading from "@/services/UseBatchLoading"

  import { showSpinnerMethod } from "@/utils/showSpinner"
  import applyDecorators from "@/utils/applyDecorators"
  import { debounceMethod } from "@/utils/debounce"

  import PdfDocument from "@/utils/pdf"

  @Options({
    name: "TransactionList",
    components: {
      Loading,
      DatePicker,
      TransactionItem,
      ModelListSelect,
    },
    props: {
      recipient: Object,
      account: Object,
      showAll: Boolean,
      caller: Object,
    },
    expose: [
      "downloadCsvFile",
      "shareCsvFile",
      "downloadPdfFile",
      "sharePdfFile",
    ],

    data(this: any) {
      return {
        exportDate: ["", ""],
        datePickerShow: false,
        selectorLabels: {
          day: this.$gettext("day"),
          week: this.$gettext("week"),
          month: this.$gettext("month"),
          year: this.$gettext("year"),
        },
        selectorsOrder: ["day", "week", "month", "year"],
        selectedTimeSpanType: "",
        selectedTimeSpanOffset: 0,
        selectedRecipientIdx: null,
        recipientBatchLoader: null,
        transactionBatchLoader: {},
      }
    },

    created() {
      let account
      if (this.account._obj?.getTransactions) {
        account = this.account._obj
      } else {
        account = this.account._obj.parent
      }
      const backend = account.parent
      const searchRecipients = backend.searchRecipients
      this.recipientBatchLoader = UseBatchLoading({
        genFactory: searchRecipients.bind(backend),
        needMorePredicate: () => {
          const div = this.$recipients
          if (!div) return false
          return div.scrollHeight - (div.scrollTop + div.offsetHeight) <= 50
        },
        onError: (e) => {
          this.$msg.error(
            this.$gettext(
              "An unexpected issue occured while downloading recipient list"
            )
          )
          throw e
        },
      })
      this.transactionBatchLoader = UseBatchLoading({
        genFactory: this.getTransactions.bind(this),
        needMorePredicate: () => {
          const div = this.$refs.transactionsContainer
          if (!div) return false
          return div.scrollHeight - (div.scrollTop + div.offsetHeight) <= 500
        },
        onError: () => {
          this.$msg.error(
            this.$gettext(
              "An unexpected issue occured while downloading transaction list"
            )
          )
        },
      })
    },

    mounted() {
      const $recipients = this.$el.querySelector(".menu")
      this._recipientsScroll = new AbortController()

      $recipients.addEventListener(
        "scroll",
        this.recipientBatchLoader.getNextElements.bind(
          this.recipientBatchLoader
        ),
        this._recipientsScroll
      )
      this.$recipients = $recipients
      this.recipientBatchLoader.newGen("")

      this.transactionBatchLoader.newGen("")
    },
    beforeUnmount() {
      this._recipientsScroll?.abort()
    },
    computed: {
      isSelectionCurrent(): boolean {
        return moment().isBetween(this.exportDate[0], this.exportDate[1])
      },
      selectedTimeSpan() {
        const now = moment().toDate()
        const timeSpanType = this.selectedTimeSpanType
        const offset = this.selectedTimeSpanOffset
        const dateSelected = moment(now)
          .subtract(-offset, timeSpanType)
          .toDate()
        const [begin, end] = [
          moment(dateSelected).startOf(timeSpanType),
          moment(dateSelected).endOf(timeSpanType),
        ].map((m) => m.toDate())

        return [begin, now < end ? now : end]
      },
      ...mapModuleState("lokapi", ["userProfile"]),
      ...mapGetters(["numericFormat"]),
    },

    methods: {
      async *getTransactions() {
        const account = this.account
        let gen
        const genOpts = { showTxListForDisabledAccount: this.showAll }
        let selectedRecipientName = null

        if (account._obj?.getTransactions) {
          gen = account._obj.getTransactions(genOpts)
        } else {
          gen = account._obj.parent.getTransactions(genOpts)
        }
        selectedRecipientName =
          this.recipientBatchLoader.elements[this.selectedRecipientIdx]?.name

        const [dateBegin, dateEnd] = this.exportDate
        for await (const t of gen) {
          if (dateBegin && t.date < dateBegin) break
          if (selectedRecipientName && selectedRecipientName !== t.related)
            continue
          if (dateEnd && t.date > dateEnd) continue
          yield t
        }
      },
      disabledDates(date: Date) {
        return date > moment().endOf("day").toDate()
      },
      async onRecipientSearch(recipientsSearchString: any) {
        if (
          this.selectedRecipientIdx !== null &&
          recipientsSearchString === ""
        ) {
          return
        }
        if (
          recipientsSearchString.length > 2 ||
          recipientsSearchString.length === 0
        ) {
          this.recipientBatchLoader.newGen(recipientsSearchString)
        }
      },

      getSubAccounts() {
        const [opts] = this.$modal.args.value
        const { account } = opts.params
        return account?.subAccounts || []
      },
      async createCsvFile() {
        const transactions = []
        const [dateBegin, dateEnd] = this.exportDate

        const hasMoreThanOneSubAccount = this.getSubAccounts().length > 1
        try {
          for await (const t of this.getTransactions()) {
            transactions.push(t)
          }
        } catch (e) {
          this.$msg.error(
            this.$gettext(
              "An unexpected issue occured while downloading transaction list"
            )
          )
          throw e
        }

        let exportFileName
        if (dateBegin && dateEnd) {
          const dateBeginStr = moment(dateBegin).format("YYYY-MM-DD")
          const dateEndStr = moment(dateEnd).format("YYYY-MM-DD")
          exportFileName = `transactions_${dateBeginStr}_${dateEndStr}.csv`
        } else {
          exportFileName = "transactions.csv"
        }

        const columnOrder = [
          "sender",
          "receiver",
          "amount",
          "date",
          "description",
          ...(hasMoreThanOneSubAccount ? ["account"] : []),
        ]

        const csvDataLine: { [key: string]: string }[] = [
          {
            sender: this.$gettext("Source"),
            receiver: this.$gettext("Target"),
            amount: this.$gettext("Amount"),
            date: this.$gettext("Date"),
            description: this.$gettext("Description"),
            ...(hasMoreThanOneSubAccount && {
              account: this.$gettext("Account"),
            }),
          },
        ]

        for (let e of transactions) {
          const name = e.related
          const [sender, receiver] = e.amount.startsWith("-")
            ? [this.userProfile.name, name]
            : [name, this.userProfile.name]
          const accountType = e.tags.includes("barter")
            ? this.$gettext("mutual")
            : this.$gettext("reconvertible")
          const data: { [key: string]: string } = {
            sender,
            receiver,
            amount: this.numericFormat(e.amount),
            date: moment(e.date).format("YYYY-MM-DD HH:mm:ss"),
            description: e.description || "",
            ...(hasMoreThanOneSubAccount && {
              account: accountType,
            }),
          }

          for (const s of columnOrder) {
            data[s] = '"' + data[s].replaceAll('"', '""') + '"'
          }

          csvDataLine.push(data)
        }

        return {
          csvContent:
            csvDataLine
              .map((dataLine) =>
                columnOrder.map((header) => dataLine[header]).join(",")
              )
              .join("\r\n") + "\r\n",
          exportFileName,
        }
      },

      getTransactionListExportMessage(this: any): string {
        let dateBeginStr, dateEndStr
        if (this.exportDate[0] && this.exportDate[1]) {
          dateBeginStr = moment(this.exportDate[0]).format("YYYY-MM-DD")
          dateEndStr = moment(this.exportDate[1]).format("YYYY-MM-DD")
        } else {
          dateBeginStr = ""
          dateEndStr = ""
        }

        return dateBeginStr && dateEndStr
          ? this.$gettext(
              "Transaction list from %{ dateBeginStr } to %{ dateEndStr }",
              {
                dateBeginStr,
                dateEndStr,
              }
            )
          : this.$gettext("Transaction list")
      },

      async createPdfReport(this: any) {
        const [dateBegin, dateEnd] = this.exportDate

        let exportFileName
        if (dateBegin && dateEnd) {
          const dateBeginStr = moment(dateBegin).format("YYYY-MM-DD")
          const dateEndStr = moment(dateEnd).format("YYYY-MM-DD")
          exportFileName = `transactions_${dateBeginStr}_${dateEndStr}.pdf`
        } else {
          exportFileName = "transactions.pdf"
        }

        const toBigInt = (amount: string) => {
          const [int, frac = ""] = amount.split(".")
          return BigInt(int + frac.padEnd(2, "0").slice(0, 2))
        }

        const addAmountUnit = async function* (txIter: any) {
          for await (const tx of txIter) {
            tx.amountUnit = toBigInt(String(tx.amount))
            yield tx
          }
        }

        let reportContactInfo
        if (this.caller) {
          const targetUserAccount = this.account._obj?.getTransactions
            ? this.account._obj
            : this.account._obj.parent
          reportContactInfo = await targetUserAccount.getContactInformation(
            this.caller
          )
        } else {
          reportContactInfo = await this.$lokapi.getReportContactInformation()
        }

        const report = new PdfDocument(
          {
            issuer: reportContactInfo.issuer,
            user: reportContactInfo.user,
            date: {
              begin: dateBegin,
              end: dateEnd,
            },
          },
          addAmountUnit(this.getTransactions()),
          this
        )

        await report.generate()

        return {
          exportFileName,
          report,
        }
      },

      downloadPdfFile: applyDecorators(
        [debounceMethod, showSpinnerMethod(".modal-card-body")],
        async function (this: any): Promise<void> {
          this.isTransactionsLoading = true
          let pdfReport

          try {
            pdfReport = await this.createPdfReport()
            pdfReport.report.save(pdfReport.exportFileName)
          } catch (e) {
            throw new UIError(
              this.$gettext("An error occured while creating the PDF file"),
              e
            )
          } finally {
            this.isTransactionsLoading = false
          }

          //this.$modal.close()
          this.$msg.success(this.$gettext("Transaction list downloaded"))
        }
      ),

      async sharePdfFile() {
        let pdfReport

        try {
          pdfReport = await this.createPdfReport()
        } catch (e) {
          throw new UIError(
            this.$gettext("An error occured while creating the PDF file"),
            e
          )
        }

        const message = this.getTransactionListExportMessage()
        const pdfDataUri = pdfReport.report.output("datauristring")
        const pdfBase64 = pdfDataUri.replace(
          /^data:application\/pdf;filename=[^;]*;base64,|^data:application\/pdf;base64,/,
          ""
        )

        try {
          await this.$export.shareBase64(
            pdfBase64,
            pdfReport.exportFileName,
            message
          )
        } catch (e) {
          this.$msg.error(this.$gettext("Transaction list could not be shared"))
          throw e
        }
        this.$modal.close()
        this.$msg.success(this.$gettext("Transaction list shared"))
      },

      downloadCsvFile: applyDecorators(
        [debounceMethod, showSpinnerMethod(".modal-card-body")],
        async function (this: any): Promise<void> {
          let csvComponents
          try {
            csvComponents = await this.createCsvFile()
          } catch (e) {
            throw new UIError(
              this.$gettext("An error occured while creating the CSV file"),
              e
            )
          }
          const { csvContent, exportFileName } = csvComponents

          try {
            await this.$export.download(csvContent, exportFileName, "text/csv")
          } catch (e) {
            this.$msg.error(
              this.$gettext("Transaction list could not be downloaded")
            )
            throw e
          }
          this.$modal.close()
          this.$msg.success(this.$gettext("Transaction list downloaded"))
        }
      ),
      async shareCsvFile() {
        const { csvContent, exportFileName } = await this.createCsvFile()
        const message = this.getTransactionListExportMessage()

        try {
          await this.$export.share(csvContent, exportFileName, message)
        } catch (e) {
          this.$msg.error(this.$gettext("Transaction list could not be shared"))
          throw e
        }
        this.$modal.close()
        this.$msg.success(this.$gettext("Transaction list shared"))
      },
    },
    watch: {
      selectedRecipientIdx: async function (newIdx, oldIdx): Promise<void> {
        this.onRecipientSearch("")
        this.transactionBatchLoader.newGen()
      },
      exportDate: async function (newExportDate): Promise<void> {
        const [newBegin, newEnd] = newExportDate
        const [normBegin, normEnd] = [
          newBegin ? moment(newBegin).startOf("day").toDate() : null,
          newEnd ? moment(newEnd).endOf("day").toDate() : null,
        ]
        if (
          normBegin &&
          normEnd &&
          (+newBegin != +normBegin || +newEnd != +normEnd)
        ) {
          this.exportDate = [normBegin, normEnd]
          return
        }
        this.transactionBatchLoader.newGen()
      },
    },
  })
  export default class TransactionList extends Vue {}
</script>
<style lang="scss">
  @import "@/assets/custom-variables";

  div.selected {
    background-color: $color-1;
  }

  div.timespan {
    padding: 0;
    margin: 0;
    border-radius: 2em;
    width: 15em;
    display: grid;
    grid-template-columns: 2em 5em 2em 6em;

    button.xmx-btn {
      text-align: center;
      border-radius: 2em;

      &.confirm {
        margin-left: 1em;
        &,
        &:hover {
          background-color: $color-2;
          color: $color-1;
        }
      }
    }
  }
  .datepicker-export {
    .xmx-datepicker-range {
      width: auto !important;
    }
  }
  div.xmx-datepicker-content {
    user-select: none;
  }
  .filter-area {
    background: #f0faf9;
  }
  .recipient-filter {
    width: 16.2em;
  }
  .recipient-filter-reset {
    position: relative;
    right: 1.5em;
    top: 0.7em;
    opacity: 0.5;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    z-index: 99;
  }
  .recipient-filter-input {
    width: 100%;
  }
  .loader-container {
    position: relative;
    height: 80px;
  }
  .ui.fluid.dropdown > .dropdown.icon {
    display: none;
  }
  button.disable {
    pointer-events: none;
  }
  @media only screen and (min-height: 1024px) {
    .ui.selection.dropdown .menu {
      max-height: 20em !important;
    }
  }
  @media only screen and (max-height: 1023px) and (min-height: 768px) {
    .ui.selection.dropdown .menu {
      max-height: 12em !important;
    }
  }
  @media only screen and (max-height: 767px) {
    .ui.selection.dropdown .menu {
      max-height: 7em !important;
    }
  }
  .tx-list-container {
    background-color: #f0faf9;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .tx-list-container section.modal-card-body {
    padding: 0.5em;
  }
</style>
